import { createSchemaPropertyAccessor, type FormConfig, workflowsEnabled } from '@takeshape/schema';
import { isDefined } from '@takeshape/util';
import { getUserPreferences } from 'app/auth/selectors';
import type { Sort } from 'app/common/types';
import { getSearchState } from 'app/content-search/selectors';
import { getMultiSelectState } from 'app/multi-select/selectors';
import { getApiVersion, getFormByName, getProjectId, getSchema, getShapeByName, getShapeIdByName } from 'app/schema';
import type { List } from 'immutable';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import CollabCell from '../collab/components/collab-cell/collab-cell';
import type { TableColumn } from '../common/components/table/types';
import type { ContentSearchState } from '../content-search';
import type { MultiSelectState } from '../multi-select';
import SelectAllCell from '../multi-select/components/select-all-cell';
import SelectCell from '../multi-select/components/select-cell';
import { isImageSchema } from '../schema/util/is-image-schema';
import { getData } from '../schema-form';
import { TableConfigCell } from './components/table-config-cell';
import { CONFIGURATOR_FORM_NAME, CONTENT_LIST_NAME } from './constants';
import type { ContentListSlice, ContentListState } from './reducer';
import { getFirstImageProp, getOrder, getStringProps, getTableCellConfig } from './utils';

export const getState = (state: ContentListSlice): ContentListState => state[CONTENT_LIST_NAME];
export const getFormArg = (_: unknown, args: { form?: FormConfig }) => args.form;

export const getUserColumns = createSelector(
  getUserPreferences,
  getProjectId,
  getShapeIdByName,
  (preferences, projectId, shapeId) => {
    const columns = preferences?.getIn([projectId, shapeId, 'columns']);
    return columns && (columns as List<string>).toArray();
  }
);

export const getDefaultColumns = createCachedSelector(
  [getSchema, getApiVersion, getShapeByName, getFormByName],
  (projectSchema, apiVersion, shape, forms) => {
    const columns = [workflowsEnabled(apiVersion) ? '_status' : '_enabled'];

    if (shape) {
      const { schema: shapeSchema } = shape;
      const order = getOrder(shapeSchema, forms?.default);

      const firstImage = getFirstImageProp(projectSchema, shapeSchema, order);
      if (firstImage) {
        columns.push(firstImage);
      }

      columns.push(...getStringProps(shapeSchema, order, 2));
    }

    columns.push('_updatedBy', '_updatedAt');
    return columns;
  }
)((state, props) => `${getProjectId(state)}/${props.shapeName}`);

export const getConfiguredColumns = createSelector(
  getUserColumns,
  getDefaultColumns,
  getShapeByName,
  getSchema,
  (userColumns, defaultColumns, shape, projectSchema) => {
    const columns: string[] = (userColumns as string[]) || defaultColumns;
    if (shape) {
      const schemaPropertyAccessor = createSchemaPropertyAccessor(projectSchema, shape);
      return columns.filter((col) => Boolean(schemaPropertyAccessor.getValue(col)));
    }

    return columns;
  }
);

export const selectedColumnConfig: TableColumn = {
  name: 'selected',
  headerComponent: SelectAllCell,
  component: SelectCell
};

const collabColumnConfig: TableColumn = {
  name: 'collab',
  component: CollabCell,
  headerComponent: TableConfigCell
};

export const getColumns = createSelector(
  getConfiguredColumns,
  getShapeByName,
  getSchema,
  (columns, shape, projectSchema) => {
    let result: TableColumn[] = [selectedColumnConfig];
    if (shape) {
      const schemaPropertyAccessor = createSchemaPropertyAccessor(projectSchema, shape);
      result = result.concat(
        columns
          .map((name: any) => {
            const propValue = schemaPropertyAccessor.getValue(name);
            return propValue && getTableCellConfig(projectSchema, name, propValue);
          })
          .filter(isDefined)
      );
    }

    return result.concat(collabColumnConfig);
  }
);

export type TableContent = Record<string, unknown>;

export const getContent = createCachedSelector(
  getSearchState,
  getColumns,
  getMultiSelectState,
  (searchState: ContentSearchState, columns, multiSelectState: MultiSelectState) => {
    return searchState.results.map((searchResult): TableContent => {
      const id = searchResult._id;
      const selection = multiSelectState.selected;

      const result: TableContent = {
        selected: selection.includes(id),
        _id: id
      };

      for (const column of columns) {
        const value = searchResult[column.name];
        if (value !== undefined) {
          result[column.name] = value;
        }
      }

      return result;
    });
  }
)((state, props) => `${getProjectId(state)}/${props.shapeName}`);

export const getSort = createSelector(getSearchState, (searchState: ContentSearchState): Sort => searchState.sort);

const getBuiltInColumns = (apiVersion: string): string[] => {
  return workflowsEnabled(apiVersion)
    ? ['_createdAt', '_updatedAt', '_createdBy', '_updatedBy', '_status']
    : ['_enabled', '_enabledAt', '_createdAt', '_updatedAt', '_createdBy', '_updatedBy'];
};

const formProps = { formName: CONFIGURATOR_FORM_NAME, path: 'columns' };
const getSelectedColumns = (state: unknown): List<string> => getData(state, formProps) as List<string>;

export type AvailableColumn = {
  name: string;
  enabled: boolean;
};

export const getAvailableColumns = createCachedSelector(
  getApiVersion,
  getShapeByName,
  getFormByName,
  getSelectedColumns,
  getSchema,

  (apiVersion, shape, forms, selectedColumns, projectSchema): AvailableColumn[] => {
    if (!shape) {
      return [];
    }

    const order = getBuiltInColumns(apiVersion).concat(getOrder(shape.schema, forms?.default));
    const columnSet = new Set(selectedColumns ? selectedColumns.toArray() : []);
    const schemaPropertyAccessor = createSchemaPropertyAccessor(projectSchema, shape);
    return order
      .filter((name) => {
        const schema = schemaPropertyAccessor.getValue(name);
        return (
          schema &&
          (isImageSchema(projectSchema, schema) ||
            (schema.type !== 'object' && schema.type !== 'array' && schema.type !== undefined))
        );
      })
      .map((name) => ({
        name,
        enabled: !columnSet.has(name)
      }));
  }
)((state, props) => `${getProjectId(state)}/${props.shapeName}`);
